import { z } from 'zod';

/**
 * Ephemeral typing-presence contract, shared by member, internal-user, and AI
 * publishers. Typing is transport-only: it never touches Postgres, the outbox,
 * domain events, push, analytics, memory, evaluation, or Pangea egress.
 *
 * The input is what an authenticated caller may submit — nothing more. The
 * event is what the server publishes after resolving the caller to an active
 * participant: publisher identity, event id, the emission stamp, and the
 * lifetime are always server-stamped, so presence cannot be spoofed or
 * extended by a client.
 *
 * Expiry is TTL-on-receipt: the event carries a bounded `ttlMs` duration and
 * each client computes the indicator's end from its OWN receipt time plus
 * `ttlMs`. An absolute expiry instant would drift on skewed client clocks —
 * a device 30 seconds behind would hold indicators 30 seconds too long. The
 * contract therefore performs no instant comparison or arithmetic at all;
 * `emittedAt` (Unix epoch milliseconds) is stamped metadata for ordering and
 * debugging, with nothing computed against it.
 */

export const TYPING_STATE = ['started', 'stopped'] as const;
export const typingStateSchema = z.enum(TYPING_STATE);
export type TypingState = z.infer<typeof typingStateSchema>;

/** The longest a typing indicator may live without a fresh heartbeat. */
export const TYPING_EXPIRY_MAX_MS = 5_000;

export const conversationTypingInputSchema = z
  .object({
    conversationId: z.string().uuid(),
    state: typingStateSchema,
  })
  .strict();
export type ConversationTypingInput = z.infer<typeof conversationTypingInputSchema>;

export const conversationTypingEventSchema = conversationTypingInputSchema
  .extend({
    participantId: z.string().uuid(),
    eventId: z.string().uuid(),
    emittedAt: z.number().int().nonnegative().safe(),
    ttlMs: z.number().int().min(1).max(TYPING_EXPIRY_MAX_MS),
  })
  .strict();
export type ConversationTypingEvent = z.infer<typeof conversationTypingEventSchema>;

export const PRESENCE_EXPIRY_MAX_MS = 5_000;

export const conversationPresenceInputSchema = z
  .object({
    conversationId: z.string().uuid(),
  })
  .strict();
export type ConversationPresenceInput = z.infer<typeof conversationPresenceInputSchema>;
